import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import {
    getSiteSettingsRoute,
    updateSiteSettingsRoute,
    uploadBannerImageRoute,
} from '../../../api/routes';
import DateSelect from '../../../components/forms/DateSelect/DateSelect';
import HMSTimeField from '../../../components/forms/HMSTimeField/HMSTimeField';
import { getLongDateString, getDefaultTimeZoneListData } from '../../../helpers/Dates';
import { getTimezoneOptions } from '../../../helpers/TimeZones';
import TimezoneSelector from '../../../components/TimezoneSelector/TimezoneSelector';
import config from '../../../config';
import '../SiteAdmin.scss';

// ── Helpers ───────────────────────────────────────────────────────────────────

// Prepend apiBaseUrl so relative /files/... paths resolve to the backend in dev
const toDisplayUrl = (url) => (url && url.startsWith('/') ? `${config.apiBaseUrl}${url}` : (url || ''));

const DEFAULT_SETTINGS = {
    infoBar: {
        isVisible: true,
        showLozenge: true,
        lozengeColor: '#FF0000',
        lozengeTextColor: '#FFFFFF',
        lozengeText: 'DOWNTIME MAINTENANCE',
        messageParts: [{ type: 'text', content: 'iCast will be down soon.' }],
        showRegionDropdown: true,
    },
    banner: {
        isVisible: true,
        backgroundColor: '#9146FF',
        imageUrl: '',
        imageObjectFit: 'cover',
    },
};

// Format a ms timestamp as a long date string for the preview
const formatDateValue = (ms) => {
    if (!ms) return '[Date]';
    try { return getLongDateString(Math.floor(ms)); }
    catch { return '[Date]'; }
};

// Format UTC seconds-from-midnight as H:MM AM/PM for the preview,
// adjusted by the given timezone offset (hours, may be fractional).
const formatTimeValue = (utcSecs, tzOffset = 0) => {
    if (utcSecs === undefined || utcSecs === null) return '[Time]';
    const localSecs = utcSecs + tzOffset * 3600;
    const normalised = ((localSecs % 86400) + 86400) % 86400;
    const h = Math.floor(normalised / 3600);
    const m = Math.floor((normalised % 3600) / 60);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

// Normalise seconds into the 0–86399 range
const normSecs = (s) => ((s % 86400) + 86400) % 86400;

// Default midnight today timestamp for new date parts
const midnightToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

// ── Message Parts Editor ──────────────────────────────────────────────────────

const MessagePartsEditor = ({ parts, onChange, timezone }) => {
    const tzOffset = timezone?.offset ?? 0;

    const addText     = () => onChange([...parts, { type: 'text', content: '' }]);
    const addDate     = () => onChange([...parts, { type: 'date', dateValue: midnightToday() }]);
    const addTime     = () => onChange([...parts, { type: 'time', timeValue: 0 }]);

    const updatePart = (idx, patch) => {
        const next = parts.map((p, i) => (i === idx ? { ...p, ...patch } : p));
        onChange(next);
    };

    const removePart = (idx) => onChange(parts.filter((_, i) => i !== idx));

    return (
        <div className="message-parts-editor">
            {parts.map((part, idx) => (
                <div key={idx} className="message-part-row">
                    {part.type === 'text' && (
                        <input
                            className="message-part-text-input"
                            value={part.content}
                            onChange={e => updatePart(idx, { content: e.target.value })}
                            placeholder="Enter text…"
                        />
                    )}
                    {part.type === 'date' && (
                        <div className="message-part-date-wrap">
                            <DateSelect
                                id={`part_date_${idx}`}
                                allowPast={true}
                                initialDate={new Date(part.dateValue || midnightToday())}
                                selectFunc={(_id, val) => updatePart(idx, { dateValue: val.getTime() })}
                                showLabel={false}
                                calendarOffset="0"
                                lowStyle={false}
                            />
                        </div>
                    )}
                    {part.type === 'time' && (
                        <div className="message-part-time-wrap">
                            <HMSTimeField
                                label="Time"
                                showLabel={false}
                                id={`part_time_${idx}`}
                                // Display stored UTC value adjusted to the selected timezone
                                value={normSecs((part.timeValue || 0) + tzOffset * 3600)}
                                // Convert the entered local time back to UTC before storing
                                setFunc={val => updatePart(idx, { timeValue: normSecs(val - tzOffset * 3600) })}
                                maxHours={23}
                                showSeconds={false}
                                relativeTime={false}
                                narrow={true}
                                centered={false}
                                showError={false}
                                error=""
                            />
                        </div>
                    )}
                    <button className="message-part-remove-btn" onClick={() => removePart(idx)} title="Remove">×</button>
                </div>
            ))}
            <div className="add-part-btns">
                <div className="standard-button button-active sa-add-part-btn" onClick={addText}>+ Text</div>
                <div className="standard-button button-active sa-add-part-btn" onClick={addDate}>+ Date</div>
                <div className="standard-button button-active sa-add-part-btn" onClick={addTime}>+ Time</div>
            </div>
        </div>
    );
};

// ── Right-side panel: InfoBar options ─────────────────────────────────────────

const InfoBarPanel = ({ settings, onChange, timezone, onTimezoneChange }) => {
    const bar = settings.infoBar;
    const set = (patch) => onChange({ ...settings, infoBar: { ...bar, ...patch } });

    return (
        <>
            <p className="site-admin-panel-title">Info Bar</p>

            <div className="sa-controls-row-2col">
                <div className="sa-controls-col">
                    <div className="sa-form-row">
                        <div className="sa-toggle-row">
                            <span className="sa-label">Show info bar</span>
                            <input type="checkbox" checked={bar.isVisible} onChange={e => set({ isVisible: e.target.checked })} />
                        </div>
                    </div>
                    <div className="sa-form-row">
                        <div className="sa-toggle-row">
                            <span className="sa-label">Show lozenge</span>
                            <input type="checkbox" checked={bar.showLozenge} onChange={e => set({ showLozenge: e.target.checked })} />
                        </div>
                    </div>
                    <div className="sa-form-row">
                        <div className="sa-toggle-row">
                            <span className="sa-label">Show region dropdown</span>
                            <input
                                type="checkbox"
                                checked={bar.showRegionDropdown}
                                onChange={e => set({ showRegionDropdown: e.target.checked })}
                            />
                        </div>
                    </div>
                </div>
                <div className="sa-controls-col">
                    {bar.showLozenge && (
                        <>
                            <div className="sa-form-row">
                                <span className="sa-label">Lozenge text</span>
                                <input
                                    className="sa-text-input"
                                    value={bar.lozengeText}
                                    onChange={e => set({ lozengeText: e.target.value })}
                                />
                            </div>
                            <div className="sa-form-row">
                                <span className="sa-label">Lozenge background colour</span>
                                <div className="sa-color-row">
                                    <input
                                        type="color"
                                        value={bar.lozengeColor}
                                        onChange={e => set({ lozengeColor: e.target.value })}
                                        title="Lozenge background colour"
                                        style={{ width: '36px', height: '28px', padding: '1px', border: 'none', cursor: 'pointer' }}
                                    />
                                    <input
                                        className="sa-color-input"
                                        value={bar.lozengeColor}
                                        onChange={e => set({ lozengeColor: e.target.value })}
                                        placeholder="#FF0000"
                                    />
                                </div>
                            </div>
                            <div className="sa-form-row">
                                <span className="sa-label">Lozenge text colour</span>
                                <div className="sa-color-row">
                                    <input
                                        type="color"
                                        value={bar.lozengeTextColor || '#FFFFFF'}
                                        onChange={e => set({ lozengeTextColor: e.target.value })}
                                        title="Lozenge text colour"
                                        style={{ width: '36px', height: '28px', padding: '1px', border: 'none', cursor: 'pointer' }}
                                    />
                                    <input
                                        className="sa-color-input"
                                        value={bar.lozengeTextColor || '#FFFFFF'}
                                        onChange={e => set({ lozengeTextColor: e.target.value })}
                                        placeholder="#FFFFFF"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="sa-form-row sa-timezone-row">
                <span className="sa-label">Message timezone</span>
                <div className="sa-timezone-selector-wrap">
                    <TimezoneSelector
                        value={timezone}
                        onChange={onTimezoneChange}
                        className="sa-tz-selector"
                        align="left"
                    />
                    <span className="sa-timezone-hint">Times entered below will be stored as UTC and displayed in the viewer's chosen timezone.</span>
                </div>
            </div>

            <div className="sa-form-row">
                <span className="sa-label">Message</span>
                <MessagePartsEditor
                    parts={bar.messageParts || []}
                    onChange={parts => set({ messageParts: parts })}
                    timezone={timezone}
                />
            </div>
        </>
    );
};

// ── Right-side panel: Banner options ──────────────────────────────────────────

const BannerPanel = ({ settings, onChange, onUpload }) => {
    const ban = settings.banner;
    const set = (patch) => onChange({ ...settings, banner: { ...ban, ...patch } });
    const fileRef = useRef();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = await onUpload(file);
        if (url) set({ imageUrl: url });
    };

    return (
        <>
            <p className="site-admin-panel-title">Banner</p>

            <div className="sa-controls-row-2col">
                <div className="sa-controls-col">
                    <div className="sa-form-row">
                        <div className="sa-toggle-row">
                            <span className="sa-label">Show banner</span>
                            <input type="checkbox" checked={ban.isVisible} onChange={e => set({ isVisible: e.target.checked })} />
                        </div>
                    </div>
                </div>
                <div className="sa-controls-col">
                    <div className="sa-form-row">
                        <span className="sa-label">Background colour</span>
                        <div className="sa-color-row">
                            <input
                                type="color"
                                value={ban.backgroundColor}
                                onChange={e => set({ backgroundColor: e.target.value })}
                                style={{ width: '36px', height: '28px', padding: '1px', border: 'none', cursor: 'pointer' }}
                            />
                            <input
                                className="sa-color-input"
                                value={ban.backgroundColor}
                                onChange={e => set({ backgroundColor: e.target.value })}
                                placeholder="#9146FF"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="sa-form-row">
                <span className="sa-label">Banner image</span>
                <div className="sa-upload-area" onClick={() => fileRef.current?.click()}>
                    {ban.imageUrl
                        ? <img src={toDisplayUrl(ban.imageUrl)} alt="Banner" style={{ maxWidth: '100%', maxHeight: '60px', objectFit: 'contain' }} />
                        : <span>Click to upload image</span>
                    }
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} />
                </div>
                <p className="sa-image-hint">
                    Recommended: 1440 × 180 px (16:1.8 ratio) · max 8 MB
                </p>
            </div>

            <div className="sa-form-row sa-form-row--inline">
                <span className="sa-label">Image fit</span>
                <select
                    className="sa-select sa-select--narrow"
                    value={ban.imageObjectFit}
                    onChange={e => set({ imageObjectFit: e.target.value })}
                >
                    <option value="cover">Cover (fills banner, may crop)</option>
                    <option value="contain">Contain (shows whole image)</option>
                </select>
            </div>
        </>
    );
};

// ── Live preview ──────────────────────────────────────────────────────────────

const LivePreview = ({ settings, selected, onSelect, timezone }) => {
    const { infoBar, banner } = settings;
    const tzOffset = timezone?.offset ?? 0;

    const renderMessage = (parts) => parts.map((p, i) => {
        if (p.type === 'date') return (
            <span key={i} style={{ fontStyle: 'italic' }}>
                {formatDateValue(p.dateValue)}
            </span>
        );
        if (p.type === 'time') return (
            <span key={i} style={{ fontStyle: 'italic' }}>
                {formatTimeValue(p.timeValue, tzOffset)}
            </span>
        );
        return <span key={i}>{p.content}</span>;
    });

    return (
        <div>
            <p className="preview-section-label">Live preview — click an area to edit</p>

            {/* Info bar — always rendered; ghost (invisible + clickable placeholder) when hidden */}
            <div
                className={`preview-info-bar preview-clickable${selected === 'infoBar' ? ' selected' : ''}${!infoBar.isVisible ? ' preview-ghost' : ''}`}
                onClick={() => onSelect('infoBar')}
            >
                {!infoBar.isVisible && <span className="preview-ghost-label">Info Bar</span>}
                {infoBar.isVisible && infoBar.showLozenge && (
                    <span
                        className="preview-lozenge"
                        style={{ backgroundColor: infoBar.lozengeColor, color: infoBar.lozengeTextColor || '#fff' }}
                    >
                        {infoBar.lozengeText || 'LABEL'}
                    </span>
                )}
                {infoBar.isVisible && (
                    <span className="ptb-message">
                        {renderMessage(infoBar.messageParts || [])}
                    </span>
                )}
                {infoBar.isVisible && infoBar.showRegionDropdown && (
                    <span className="preview-region-flag">
                        <TimezoneSelector
                            value={timezone}
                            onChange={() => {}}
                            className="preview-tz"
                        />
                    </span>
                )}
                {infoBar.isVisible && (
                    <span className="preview-close-placeholder">✕</span>
                )}
            </div>

            {/* Banner — always rendered; ghost when hidden */}
            <div
                className={`preview-banner preview-clickable${selected === 'banner' ? ' selected' : ''}${!banner.isVisible ? ' preview-ghost' : ''}`}
                style={banner.isVisible ? { backgroundColor: banner.backgroundColor } : {}}
                onClick={() => onSelect('banner')}
            >
                {!banner.isVisible && <span className="preview-ghost-label">Banner</span>}
                {banner.isVisible && banner.imageUrl && (
                    <img
                        src={toDisplayUrl(banner.imageUrl)}
                        alt="Banner"
                        style={{ objectFit: banner.imageObjectFit }}
                    />
                )}
            </div>
        </div>
    );
};

// ── Main BannerEditor component ───────────────────────────────────────────────

const BannerEditor = () => {
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState('infoBar'); // 'infoBar' | 'banner'
    const [editorTimezone, setEditorTimezone] = useState(null);

    useEffect(() => {
        const opts = getTimezoneOptions();
        setEditorTimezone(getDefaultTimeZoneListData(opts));
    }, []);

    useEffect(() => {
        axios.get(getSiteSettingsRoute, { withCredentials: true })
            .then(res => {
                if (res.data.success) {
                    setSettings(res.data.settings);
                }
            })
            .catch(() => toast.error('Failed to load site settings'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const res = await axios.put(updateSiteSettingsRoute, {
                infoBar: settings.infoBar,
                banner: settings.banner,
            }, { withCredentials: true });
            if (res.data.success) {
                toast.success('Settings saved');
            }
        } catch {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    }, [settings]);

    const handleUpload = useCallback(async (file) => {
        const formData = new FormData();
        formData.append('bannerImage', file);
        try {
            const res = await axios.post(uploadBannerImageRoute, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (res.data.success) return res.data.url;
            toast.error('Image upload failed');
        } catch {
            toast.error('Image upload failed');
        }
        return null;
    }, []);

    if (loading) return <div className="home-loading-placeholder" />;

    return (
        <div className="site-admin-editor">
            {/* Left: live preview */}
            <div className="site-admin-preview">
                <LivePreview settings={settings} selected={selected} onSelect={setSelected} timezone={editorTimezone} />
            </div>

            {/* Right: context panel */}
            <div className="site-admin-panel">
                {selected === 'infoBar' && (
                    <InfoBarPanel
                        settings={settings}
                        onChange={setSettings}
                        timezone={editorTimezone}
                        onTimezoneChange={setEditorTimezone}
                    />
                )}
                {selected === 'banner' && (
                    <BannerPanel settings={settings} onChange={setSettings} onUpload={handleUpload} />
                )}

                <div
                    className={`standard-button button-active sa-save-btn${saving ? '' : ''}`}
                    onClick={handleSave}
                >
                    {saving ? 'Saving…' : 'Save Changes'}
                </div>
            </div>
        </div>
    );
};

export default BannerEditor;
